import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import StewartTable, {
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
import StewartTableFooterPaginator from "controls/global/stewart-table/StewartTableFooterPaginator";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import { Order } from "controls/global/stewart-table/stewartTableHelper";
import { PolicyDataErrorRecord } from "entities/UIModel/PolicyDataErrorRecord";

import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import StewartAutocompleteWithCheckboxes from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import InputSearch from "./InputSearch";
import { colors, fontSize, fontWeight } from "theme/defaultStyle";

function formatCurrency(value: number | undefined): string {
  if (!value && value !== 0) return "$0.00";
  return (
    "$" +
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatPolicyDate(
  year: number | undefined,
  month: number | undefined,
  day: number | undefined
): string {
  if (!year || !month || !day) return "---";
  return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${year}`;
}

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return "---";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

interface PolicyDataErrorsTableProps {
  records: PolicyDataErrorRecord[];
  onFiltersChange: (name: keyof PolicyDataErrorRecord, value: string | null) => void;
  onMultiSelectChange: (
    e: { target: { name: string; value: OptionRenderType[] } },
    value: OptionRenderType[]
  ) => void;
}

const PolicyDataErrorsTable = ({
  records,
  onFiltersChange,
  onMultiSelectChange,
}: PolicyDataErrorsTableProps) => {
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof PolicyDataErrorRecord | undefined>(undefined);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (count: number) => {
    setRowsPerPage(count);
    setPage(1);
  };

  const handleRequestSort = (property: keyof PolicyDataErrorRecord) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleColumnResize = (
    _property: keyof PolicyDataErrorRecord,
    _newWidth: number
  ) => {
    // Column resize handled by StewartTable internally
  };

  // Build unique options for multi-select filters
  const errorTypeOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.policyErrorTypeName).filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [records]);

  const agentOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.agentID).filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [records]);

  const stateOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.stateCode).filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [records]);

  const sourceOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.originationSource).filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [records]);

  const countyOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.countyCode).filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [records]);

  const productTypeOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.productTypeAbbrTypeCode).filter(Boolean))];
    return unique.sort().map((v) => {
      const name = records.find((r) => r.productTypeAbbrTypeCode === v)?.productTypeAbbrTypeName;
      return { label: name ? `${v} — ${name}` : v, value: v };
    });
  }, [records]);

  const recordTypeOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.recordTypeCode).filter(Boolean))];
    return unique.sort().map((v) => {
      const name = records.find((r) => r.recordTypeCode === v)?.recordTypeName;
      return { label: name ? `${v} — ${name}` : v, value: v };
    });
  }, [records]);

  const propertyTypeOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.propertyTypeCode).filter(Boolean))];
    return unique.sort().map((v) => {
      const name = records.find((r) => r.propertyTypeCode === v)?.propertyTypeName;
      return { label: name ? `${v} — ${name}` : v, value: v };
    });
  }, [records]);

  const columns: StewartTableColumn<PolicyDataErrorRecord>[] = useMemo(
    () => [
      // Policy Error
      {
        field: "policyErrorTypeName",
        name: "Validation Error",
        width: 180,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 1,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="policyErrorTypeName"
            onChange={onMultiSelectChange}
            options={errorTypeOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <Box
            component="span"
            sx={{ fontWeight: fontWeight.bold1, fontSize: fontSize.base }}
          >
            {row.policyErrorTypeName}
          </Box>
        ),
      },
      {
        field: "policyErrorExceptionMessage",
        name: "Error Message",
        width: 280,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 2,
        actionComponent: () => (
          <InputSearch name="policyErrorExceptionMessage" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.policyErrorExceptionMessage || ""} />
        ),
      },
      {
        field: "policyErrorStatusTypeCode",
        name: "Status",
        width: 90,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 3,
        valueGetter: (row: PolicyDataErrorRecord) => {
          const isResolved = row.policyErrorStatusTypeCode === "RESOLVED";
          return (
            <Box
              component="span"
              sx={{
                fontSize: fontSize.xsmall,
                fontWeight: fontWeight.bold2,
                borderRadius: "3px",
                padding: "2px 6px",
                backgroundColor: isResolved ? colors.green : colors.red01,
                color: colors.white,
              }}
            >
              {row.policyErrorStatusTypeCode}
            </Box>
          );
        },
      },
      {
        field: "policyErrorExceptionDate",
        name: "Exception Date",
        width: 150,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 4,
        actionComponent: () => (
          <InputSearch name="policyErrorExceptionDate" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={formatDateTime(row.policyErrorExceptionDate)} />
        ),
      },
      // Policy Identity
      {
        field: "policyNumber",
        name: "Policy Number",
        width: 145,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 5,
        actionComponent: () => (
          <InputSearch name="policyNumber" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <Box
            component="span"
            sx={{ color: colors.blue15, fontWeight: fontWeight.bold1, fontSize: fontSize.base }}
          >
            {row.policyNumber}
          </Box>
        ),
      },
      {
        field: "fileNumber",
        name: "File Number",
        width: 120,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 6,
        actionComponent: () => (
          <InputSearch name="fileNumber" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.fileNumber || "---"} />
        ),
      },
      {
        field: "agentID",
        name: "Agency",
        width: 110,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 7,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="agentID"
            onChange={onMultiSelectChange}
            options={agentOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.agentID || ""} />
        ),
      },
      {
        field: "stateCode",
        name: "State",
        width: 80,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 8,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="stateCode"
            onChange={onMultiSelectChange}
            options={stateOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.stateCode || ""} />
        ),
      },
      {
        field: "originationSource",
        name: "Source",
        width: 120,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 9,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="originationSource"
            onChange={onMultiSelectChange}
            options={sourceOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.originationSource || ""} />
        ),
      },
      {
        field: "countyCode",
        name: "County",
        width: 140,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 10,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="countyCode"
            onChange={onMultiSelectChange}
            options={countyOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.countyCode || "---"} />
        ),
      },
      {
        field: "policyYear",
        name: "Policy Date",
        width: 110,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 11,
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell
            title={formatPolicyDate(row.policyYear, row.policyMonth, row.policyDay)}
          />
        ),
      },
      {
        field: "productTypeAbbrTypeCode",
        name: "Policy Type",
        width: 130,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 12,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="productTypeAbbrTypeCode"
            onChange={onMultiSelectChange}
            options={productTypeOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell
            title={
              row.productTypeAbbrTypeName
                ? `${row.productTypeAbbrTypeCode} — ${row.productTypeAbbrTypeName}`
                : row.productTypeAbbrTypeCode || "---"
            }
          />
        ),
      },
      // Transaction
      {
        field: "transCode",
        name: "Trans Code",
        width: 90,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 13,
        actionComponent: () => (
          <InputSearch name="transCode" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.transCode || ""} />
        ),
      },
      {
        field: "recordTypeCode",
        name: "Record Type",
        width: 120,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 14,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="recordTypeCode"
            onChange={onMultiSelectChange}
            options={recordTypeOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell
            title={
              row.recordTypeName
                ? `${row.recordTypeCode} — ${row.recordTypeName}`
                : row.recordTypeCode || "---"
            }
          />
        ),
      },
      {
        field: "propertyTypeCode",
        name: "Property Type",
        width: 120,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 15,
        actionComponent: () => (
          <StewartAutocompleteWithCheckboxes
            name="propertyTypeCode"
            onChange={onMultiSelectChange}
            options={propertyTypeOptions}
            placeholder="Filter"
          />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell
            title={
              row.propertyTypeName
                ? `${row.propertyTypeCode} — ${row.propertyTypeName}`
                : row.propertyTypeCode || "---"
            }
          />
        ),
      },
      {
        field: "batchNumber",
        name: "Batch Number",
        width: 110,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 16,
        actionComponent: () => (
          <InputSearch name="batchNumber" onChange={onFiltersChange} />
        ),
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={row.batchNumber || ""} />
        ),
      },
      // Financials
      {
        field: "liabilityAmount",
        name: "Liability",
        width: 120,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 17,
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={formatCurrency(row.liabilityAmount)} />
        ),
      },
      {
        field: "actualFee",
        name: "Premium",
        width: 100,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 18,
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={formatCurrency(row.actualFee)} />
        ),
      },
      {
        field: "actualRetention",
        name: "Retention",
        width: 100,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 19,
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={formatCurrency(row.actualRetention)} />
        ),
      },
      {
        field: "actualRiskRate",
        name: "Risk Rate",
        width: 100,
        sortable: true,
        resizable: true,
        draggable: true,
        position: 20,
        valueGetter: (row: PolicyDataErrorRecord) => (
          <TooltipCell title={formatCurrency(row.actualRiskRate)} />
        ),
      },
    ],
    [
      errorTypeOptions,
      agentOptions,
      stateOptions,
      sourceOptions,
      countyOptions,
      productTypeOptions,
      recordTypeOptions,
      propertyTypeOptions,
      onFiltersChange,
      onMultiSelectChange,
    ]
  );

  // Default hidden columns — only show the 8 default visible columns from the HTML reference
  const defaultHiddenColumns: (keyof PolicyDataErrorRecord)[] = useMemo(
    () => [
      "policyErrorStatusTypeCode",
      "countyCode",
      "policyYear",
      "productTypeAbbrTypeCode",
      "transCode",
      "recordTypeCode",
      "propertyTypeCode",
      "batchNumber",
      "liabilityAmount",
      "actualFee",
      "actualRetention",
      "actualRiskRate",
    ],
    []
  );

  const emptyStateType =
    records.length === 0 ? EmptyStateType.NO_RESULTS : EmptyStateType.DEFAULT;

  const noRowsMessage = ["Please adjust your search criteria and search again."];

  return (
    <Box>
      <StewartTable
        cols={columns}
        rows={records}
        page={page}
        rowsPerPage={rowsPerPage}
        showActionRow={true}
        noRowsMessage={noRowsMessage}
        orderBy={orderBy}
        order={order}
        useDbPagingSorting={false}
        onRequestSort={handleRequestSort}
        onColumnResize={handleColumnResize}
        hiddenColumns={defaultHiddenColumns}
        emptyStateType={emptyStateType}
      />
      <StewartTableFooterPaginator
        totalRows={records.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        disablePagination={records.length === 0}
      />
    </Box>
  );
};

export default PolicyDataErrorsTable;
